import { useEffect, useState } from "react";
import { Loader2, Save, Truck, Phone, Type } from "lucide-react";
import { adminUpdateSettings, formatApiError, getSettings } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const inputCls =
  "mt-1.5 w-full rounded-md border border-input bg-white/60 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary transition";

const Label = ({ children }) => (
  <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">{children}</span>
);

export const SettingsTab = ({ token }) => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getSettings()
      .then(setForm)
      .catch(() => toast.error("Could not load store settings"));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        free_shipping_above: Math.max(0, parseInt(form.free_shipping_above, 10) || 0),
        shipping_fee: Math.max(0, parseInt(form.shipping_fee, 10) || 0),
      };
      const updated = await adminUpdateSettings(token, payload);
      setForm(updated);
      toast.success("Store settings saved — live on the storefront");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!form) {
    return (
      <div className="py-16 flex justify-center" data-testid="settings-loading">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <form onSubmit={save} className="max-w-2xl space-y-6" data-testid="settings-form">
      <section className="bg-white/70 border border-border/60 rounded-xl p-6">
        <h3 className="font-serif text-xl font-semibold">Storefront status</h3>
        <label className="flex items-start gap-3 cursor-pointer mt-4" data-testid="settings-maintenance-toggle">
          <input
            type="checkbox"
            checked={!!form.under_construction}
            onChange={(e) => setForm((f) => ({ ...f, under_construction: e.target.checked }))}
            className="w-4 h-4 mt-0.5 accent-[#3f5426]"
          />
          <span className="text-sm font-semibold leading-snug">
            Under construction mode
            <span className="block text-xs font-normal text-muted-foreground mt-1 leading-relaxed">
              Visitors see a "Launching soon" page on the storefront while you finish updating products.
              This admin panel stays fully accessible. Turn it off when you're ready to open the store.
            </span>
          </span>
        </label>
      </section>

      <section className="bg-white/70 border border-border/60 rounded-xl p-6">
        <h3 className="font-serif text-xl font-semibold flex items-center gap-2">
          <Type className="w-5 h-5 text-primary" /> Storefront text
        </h3>
        <p className="text-xs text-muted-foreground mt-1">
          Change the words on your storefront anytime — no need to ask anyone.
        </p>
        <div className="space-y-4 mt-5">
          <label className="block">
            <Label>Announcement bar text (top strip)</Label>
            <input
              data-testid="settings-announcement-input"
              value={form.announcement_text || ""}
              onChange={set("announcement_text")}
              className={inputCls}
            />
            <span className="text-[10px] text-muted-foreground mt-1 block">
              Tip: type {"{free_shipping}"} anywhere to auto-fill your free-shipping amount.
            </span>
          </label>
          <label className="block">
            <Label>Homepage hero intro line</Label>
            <textarea
              data-testid="settings-hero-intro-input"
              rows={2}
              value={form.hero_intro || ""}
              onChange={set("hero_intro")}
              className={`${inputCls} resize-none`}
            />
          </label>
        </div>
      </section>

      <section className="bg-white/70 border border-border/60 rounded-xl p-6">
        <h3 className="font-serif text-xl font-semibold flex items-center gap-2">
          <Truck className="w-5 h-5 text-primary" /> Shipping
        </h3>
        <p className="text-xs text-muted-foreground mt-1">
          Shown in the announcement bar, cart, checkout, product pages and applied to every order total.
        </p>
        <div className="grid sm:grid-cols-2 gap-4 mt-5">
          <label className="block">
            <Label>Free shipping above (₹)</Label>
            <input
              data-testid="settings-free-shipping-input"
              type="number"
              min="0"
              required
              value={form.free_shipping_above}
              onChange={set("free_shipping_above")}
              className={inputCls}
            />
          </label>
          <label className="block">
            <Label>Shipping fee below that (₹)</Label>
            <input
              data-testid="settings-shipping-fee-input"
              type="number"
              min="0"
              required
              value={form.shipping_fee}
              onChange={set("shipping_fee")}
              className={inputCls}
            />
          </label>
        </div>
      </section>

      <section className="bg-white/70 border border-border/60 rounded-xl p-6">
        <h3 className="font-serif text-xl font-semibold flex items-center gap-2">
          <Phone className="w-5 h-5 text-primary" /> Contact details
        </h3>
        <p className="text-xs text-muted-foreground mt-1">
          Shown in the Contact section and the footer across the store.
        </p>
        <div className="grid sm:grid-cols-2 gap-4 mt-5">
          <label className="block">
            <Label>Phone</Label>
            <input data-testid="settings-phone-input" required value={form.contact_phone} onChange={set("contact_phone")} className={inputCls} />
          </label>
          <label className="block">
            <Label>Email</Label>
            <input data-testid="settings-email-input" type="email" required value={form.contact_email} onChange={set("contact_email")} className={inputCls} />
          </label>
          <label className="block sm:col-span-2">
            <Label>Address (one line per row)</Label>
            <textarea
              data-testid="settings-address-input"
              rows={3}
              required
              value={form.contact_address}
              onChange={set("contact_address")}
              className={`${inputCls} resize-none`}
            />
          </label>
          <label className="block sm:col-span-2">
            <Label>Working hours (optional)</Label>
            <input
              data-testid="settings-hours-input"
              value={form.contact_hours || ""}
              onChange={set("contact_hours")}
              placeholder="Mon–Sat, 10 AM – 7 PM"
              className={inputCls}
            />
          </label>
        </div>
      </section>

      <button
        data-testid="settings-save-button"
        type="submit"
        disabled={saving}
        className="rounded-full bg-primary text-primary-foreground px-8 py-3 text-sm font-bold inline-flex items-center gap-2 hover:-translate-y-0.5 transition-transform disabled:opacity-60"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        {saving ? "Saving…" : "Save settings"}
      </button>
    </form>
  );
};
